/**
 * 统一缓存管理工具
 * 提供 sessionStorage/localStorage 的通用缓存封装
 */

type StorageType = 'session' | 'local';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

/**
 * 通用存储缓存类
 * @template T - 缓存数据类型
 */
export class StorageCache<T> {
  private storage: Storage | null = null;

  /**
   * @param key - 存储的 key
   * @param ttl - 缓存有效期（毫秒），0 表示永不过期
   * @param storageType - 存储类型：session 或 local
   */
  constructor(
    private key: string,
    private ttl: number = 5 * 60 * 1000,
    private storageType: StorageType = 'session'
  ) {
    if (typeof window !== 'undefined') {
      this.storage = storageType === 'local' ? localStorage : sessionStorage;
    }
  }

  /** 获取缓存数据 */
  get(): T | null {
    if (!this.storage) return null;
    try {
      const cached = this.storage.getItem(this.key);
      if (!cached) return null;

      const entry = JSON.parse(cached) as CacheEntry<T>;

      // 检查是否过期（ttl > 0 时才检查）
      if (this.ttl > 0 && Date.now() - entry.timestamp > this.ttl) {
        this.clear();
        return null;
      }

      return entry.data;
    } catch {
      return null;
    }
  }

  /** 设置缓存数据 */
  set(value: T): void {
    if (!this.storage) return;
    try {
      const entry: CacheEntry<T> = {
        data: value,
        timestamp: Date.now(),
      };
      this.storage.setItem(this.key, JSON.stringify(entry));
    } catch {
      // ignore storage errors
    }
  }

  /** 清除缓存 */
  clear(): void {
    if (!this.storage) return;
    try {
      this.storage.removeItem(this.key);
    } catch {
      // ignore
    }
  }
}

/** 缓存 key 常量 */
export const CACHE_KEYS = {
  BALANCE: 'predict_balance_cache',
  SAFE_ADDRESS: 'predict_safe_address',
  CLOB_CREDENTIALS: 'predict_clob_creds',
} as const;

/** 缓存 TTL 常量 */
export const CACHE_TTL = {
  BALANCE: 5 * 60 * 1000, // 5分钟
  PERMANENT: 0,           // 永不过期
} as const;
